import React, { useState, useEffect } from 'react';
import { Phone, MessageSquare, ArrowRightLeft, Send, Clock, Check, X, RefreshCw } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TwilioSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [smsLogs, setSmsLogs] = useState([]);
  const [forwardLogs, setForwardLogs] = useState([]);
  const [settings, setSettings] = useState({ owner_timeout: 20, enabled: true });
  
  // Test SMS form
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Test message from Front Desk Pro');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [configRes, smsRes, forwardRes, settingsRes] = await Promise.all([
        axios.get(`${API}/twilio/config`),
        axios.get(`${API}/twilio/sms-logs?limit=10`),
        axios.get(`${API}/twilio/call-forward-logs?limit=10`),
        axios.get(`${API}/twilio/call-forward-settings`)
      ]);
      setConfig(configRes.data);
      setSmsLogs(smsRes.data.logs || []);
      setForwardLogs(forwardRes.data.logs || []);
      setSettings(settingsRes.data);
    } catch (err) {
      console.error('Failed to fetch Twilio data:', err);
      toast.error('Failed to load Twilio settings');
    } finally {
      setLoading(false);
    }
  };

  const sendTestSMS = async () => {
    if (!testPhone) {
      toast.error('Enter a phone number');
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/twilio/send-sms`, {
        to_number: testPhone,
        message: testMessage
      });
      toast.success('SMS sent successfully!');
      fetchAll(); // Refresh logs
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const updateSettings = async (updates) => {
    try {
      const res = await axios.put(`${API}/twilio/call-forward-settings`, updates);
      setSettings(res.data.settings);
      toast.success('Settings updated');
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-foreground tracking-tight">TWILIO SETTINGS</h1>
          <p className="text-muted-foreground mt-1">SMS notifications & call forwarding</p>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
          <Card className="card-modern p-4" data-testid="twilio-status-card">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${config?.configured ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-foreground font-medium">
                {config?.configured ? 'Twilio Connected' : 'Twilio Not Configured'}
              </span>
            </div>
            {config?.twilio_number && (
              <p className="text-muted-foreground text-sm mt-2">
                Twilio #: {config.twilio_number}
              </p>
            )}
          </Card>

          <Card className="card-modern p-4">
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-blue-400" />
              <span className="text-foreground font-medium">Owner Phone</span>
            </div>
            <p className="text-muted-foreground text-sm mt-2">
              {config?.owner_number || 'Not set'}
            </p>
          </Card>

          <Card className="card-modern p-4">
            <div className="flex items-center gap-3">
              <ArrowRightLeft className="w-5 h-5 text-orange-400" />
              <span className="text-foreground font-medium">Pro Forwarding</span>
            </div>
            <p className="text-muted-foreground text-sm mt-2">
              {config?.retell_number || 'Not set'}
            </p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SMS Section */}
          <div className="space-y-6">
            {/* Send Test SMS */}
            <Card className="card-modern p-6">
              <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-400" />
                Send Test SMS
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Phone Number</label>
                  <Input
                    data-testid="test-phone-input"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Message</label>
                  <Input
                    data-testid="test-message-input"
                    placeholder="Your message"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    className="bg-muted border-border"
                  />
                </div>
                <Button 
                  data-testid="send-test-sms-btn"
                  onClick={sendTestSMS} 
                  disabled={sending || !config?.configured}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {sending ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  {sending ? 'Sending...' : 'Send SMS'}
                </Button>
              </div>
            </Card>

            {/* SMS Logs */}
            <Card className="card-modern p-6">
              <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                Recent SMS
              </h2>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {smsLogs.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">No SMS sent yet</p>
                ) : (
                  smsLogs.map((log, i) => (
                    <div key={i} className="p-3 rounded bg-muted text-sm">
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-foreground font-medium">{log.to}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          log.status === 'delivered' ? 'bg-green-400/20 text-green-400' :
                          log.status === 'queued' ? 'bg-yellow-400/20 text-yellow-400' :
                          'bg-gray-400/20 text-gray-400'
                        }`}>
                          {log.status}
                        </span>
                      </div>
                      <p className="text-muted-foreground truncate">{log.body}</p>
                      <p className="text-muted-foreground text-xs mt-1">{formatDate(log.created_at)}</p>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          {/* Call Forwarding Section */}
          <div className="space-y-6">
            {/* Call Forwarding Settings */}
            <Card className="card-modern p-6">
              <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-orange-400" />
                Call Forwarding
              </h2>
              <p className="text-muted-foreground text-sm mb-4">
                When someone calls your Twilio number, it rings your phone first. If you don't answer, it forwards to your Pro.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-foreground font-medium">Enable Call Forwarding</p>
                    <p className="text-muted-foreground text-sm">Ring owner before Pro</p>
                  </div>
                  <Switch
                    data-testid="call-forward-toggle"
                    checked={settings.enabled}
                    onCheckedChange={(checked) => updateSettings({ enabled: checked })}
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">
                    Ring Owner For (seconds)
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="owner-timeout-input"
                      type="number"
                      min="5"
                      max="60"
                      value={settings.owner_timeout || 20}
                      onChange={(e) => setSettings(s => ({ ...s, owner_timeout: parseInt(e.target.value) }))}
                      className="bg-muted border-border w-24"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => updateSettings({ owner_timeout: settings.owner_timeout })}
                    >
                      Save
                    </Button>
                  </div>
                  <p className="text-muted-foreground text-xs mt-1">
                    After {settings.owner_timeout || 20}s, call forwards to Pro
                  </p>
                </div>

                <div className="p-4 rounded bg-orange-500/10 border border-orange-500/20">
                  <p className="text-orange-400 text-sm font-medium mb-2">Setup Required</p>
                  <p className="text-muted-foreground text-xs">
                    To activate call forwarding, configure your Twilio phone number's webhook to:
                  </p>
                  <code className="text-xs text-orange-300 block mt-2 p-2 bg-black/30 rounded break-all">
                    {process.env.REACT_APP_BACKEND_URL}/api/twilio/voice/incoming
                  </code>
                </div>
              </div>
            </Card>

            {/* Call Forward Logs */}
            <Card className="card-modern p-6">
              <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-purple-400" />
                Call Forwarding Logs
              </h2>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {forwardLogs.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">No forwarded calls yet</p>
                ) : (
                  forwardLogs.map((log, i) => (
                    <div key={i} className="p-3 rounded bg-muted text-sm">
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-foreground font-medium">{log.from}</span>
                        <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                          log.status === 'answered_by_owner' ? 'bg-green-400/20 text-green-400' :
                          log.status === 'forwarded_to_ai' ? 'bg-blue-400/20 text-blue-400' :
                          'bg-yellow-400/20 text-yellow-400'
                        }`}>
                          {log.status === 'answered_by_owner' && <Check className="w-3 h-3" />}
                          {log.status === 'forwarded_to_ai' && <ArrowRightLeft className="w-3 h-3" />}
                          {log.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-muted-foreground text-xs">{formatDate(log.created_at)}</p>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="card-modern p-4 mt-6 bg-blue-500/10 border-blue-500/20">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-400 font-medium">How it works</p>
              <p className="text-muted-foreground text-sm mt-1">
                <strong>SMS:</strong> When Pro books an appointment, customer gets a confirmation text.<br/>
                <strong>Call Forwarding:</strong> Calls to your Twilio number ring your phone first ({settings.owner_timeout}s). 
                If you're busy, it automatically forwards to Front Desk Pro.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TwilioSettings;
